import { postMeUpdateNameMutation } from "@aura-frontend/omnibus-az-client";
import { useMutation } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { LoadingScreen } from "@/components/auth/LoadingScreen";
import { supabaseClient } from "@/config/supabase";
import { loadUserAccount } from "@/utils/createUserAccount";

/**
 * Auth callback page for OAuth redirects.
 *
 * Supabase redirects back to this route after the user completes
 * OAuth sign-in with Google, Facebook, or Apple.
 *
 * The session is automatically established by Supabase's JS SDK,
 * and the AppContext will pick it up on the next render.
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const { mutateAsync: updateName } = useMutation({
    ...postMeUpdateNameMutation(),
    onError: (error: Error) => {
      console.error("[AuthCallback] /update-name error:", error);
    },
  });

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Check for OAuth error in URL
        const urlParams = new URLSearchParams(window.location.search);
        const errorCode = urlParams.get("error_code");
        const errorDescription = urlParams.get("error_description");

        if (errorCode) {
          console.error("[OAuth] Error:", errorDescription || errorCode);
          setError(
            `OAuth Error: ${errorDescription || errorCode}. Please check Supabase OAuth configuration.`,
          );
          setTimeout(() => navigate("/login", { replace: true }), 3000);
          return;
        }

        // Wait a moment for Supabase to process the callback
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Get the current session
        const { data, error: sessionError } =
          await supabaseClient.auth.getSession();

        if (sessionError) {
          console.error("[OAuth] Session error:", sessionError.message);
        }

        if (!data?.session) {
          // Try to refresh the session explicitly
          const { data: refreshData, error: refreshError } =
            await supabaseClient.auth.refreshSession();

          if (refreshError) {
            console.error("[OAuth] Session missing and refresh failed");
            navigate("/login", { replace: true });
            return;
          }

          if (!refreshData?.session) {
            navigate("/login", { replace: true });
            return;
          }
        }

        // Create account in omnibus and save name
        try {
          const { data: authData } = await supabaseClient.auth.getUser();
          const fullName = authData.user?.user_metadata?.full_name || "";
          const [firstName, ...lastNameParts] = fullName.split(" ");
          const lastName = lastNameParts.join(" ");

          await loadUserAccount({
            firstName: firstName || undefined,
            lastName: lastName || undefined,
            onSaveProfile: updateName,
            queryClient,
          });
          console.log("[OAuth] Login successful:", authData.user?.email);
        } catch (err) {
          console.error("[OAuth] Account creation failed:", err);
          // Continue anyway - user might already exist
        }

        navigate("/", { replace: true });
      } catch (error) {
        console.error(
          "[OAuth] Unexpected error:",
          error instanceof Error ? error.message : String(error),
        );
        navigate("/login", { replace: true });
      }
    };

    handleCallback();
  }, [navigate, queryClient, updateName]);

  if (error) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl px-12 py-14 flex flex-col items-center max-w-md w-full mx-4">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Authentication Error
          </h2>
          <div className="w-full mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {error}
          </div>
          <button
            onClick={() => navigate("/login", { replace: true })}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return <LoadingScreen />;
};

export default AuthCallback;
